/**
 * Phase 4 acceptance check: the GPU tracer path matches the CPU reference
 * on a fixed seed.
 *
 * Procedure (run in the browser; Vitest runs in Node without a GPU):
 * 1. Spawn N tracers on the CPU from a fixed seed.
 * 2. Upload that exact state to the GPU.
 * 3. Step both paths the same number of times with the same dt, through
 *    the same live wind field (all components on), gust fixed at 1.
 * 4. Read the GPU buffer back and compare positions tracer by tracer.
 *
 * Tracers that respawned on either path are excluded and counted, because
 * respawn randomness differs between the paths by design (sequential
 * mulberry32 on the CPU, hashed per-index randoms on the GPU). A respawn is
 * detected as the age dropping below its starting value.
 *
 * Tolerance, fixed before the first measurement: 5 m maximum position
 * error after 120 steps of 2 s. Reasoning: float32 spacing at 1e5 m is
 * about 0.008 m; a few hundred operations per step over 120 steps can
 * plausibly accumulate to a metre or so. 5 m is far below what is visible
 * (debris pieces are drawn ~180 m across) and below 1% of typical
 * displacement over the run.
 */
import type { WebGPURenderer } from 'three/webgpu';
import { createTracers } from '../tracers';
import type { WindField } from '../windField';
import { createGpuTracers } from './gpuTracers';

export interface GpuValidationResult {
  backend: string;
  tracers: number;
  steps: number;
  dtS: number;
  compared: number;
  excludedRespawned: number;
  maxErrM: number;
  p99ErrM: number;
  medianErrM: number;
  meanDisplacementM: number;
  toleranceM: number;
  pass: boolean;
}

export const GPU_VALIDATION_TOLERANCE_M = 5;

export async function runGpuValidation(
  renderer: WebGPURenderer,
  field: WindField,
  backend: string,
  n = 10_000,
  steps = 120,
  dtS = 2,
): Promise<GpuValidationResult> {
  const rMax = field.holland.rMaxM;
  const options = {
    capacity: n,
    seed: 777,
    // Large but finite: no domain or age respawns during the run.
    domainRadiusM: 1e12,
    spawnInnerM: 0.3 * rMax,
    spawnOuterM: 4 * rMax,
    spawnMinHeightM: 50,
    spawnMaxHeightM: 3000,
    maxAgeS: 1e9,
  };
  const cpu = createTracers(options);
  cpu.setCount(n);
  const gpu = createGpuTracers(renderer, field, { ...options }, n);
  gpu.uploadFrom(cpu);

  const startX = Float64Array.from(cpu.posX);
  const startY = Float64Array.from(cpu.posY);
  const startZ = Float64Array.from(cpu.posZ);
  const startAge = Float32Array.from(cpu.age);

  const savedGust = field.gust;
  field.setGust(1);
  let t = 0;
  for (let s = 0; s < steps; s++) {
    gpu.step(dtS, t);
    cpu.step(dtS, t, field);
    t += dtS;
  }
  field.setGust(savedGust);

  const g = await gpu.readPositions();
  const errs: number[] = [];
  let excluded = 0;
  let dispSum = 0;
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const respawned =
      (cpu.age[i] as number) < (startAge[i] as number) ||
      (g[o + 3] as number) < (startAge[i] as number);
    if (respawned) {
      excluded++;
      continue;
    }
    const ex = (g[o] as number) - (cpu.posX[i] as number);
    const ey = (g[o + 1] as number) - (cpu.posY[i] as number);
    const ez = (g[o + 2] as number) - (cpu.posZ[i] as number);
    errs.push(Math.hypot(ex, ey, ez));
    dispSum += Math.hypot(
      (cpu.posX[i] as number) - (startX[i] as number),
      (cpu.posY[i] as number) - (startY[i] as number),
      (cpu.posZ[i] as number) - (startZ[i] as number),
    );
  }
  gpu.dispose();

  errs.sort((a, b) => a - b);
  const at = (q: number): number =>
    errs.length === 0
      ? Number.NaN
      : (errs[Math.min(errs.length - 1, Math.floor(q * errs.length))] as number);
  const maxErr = errs.length === 0 ? Number.NaN : (errs[errs.length - 1] as number);
  return {
    backend,
    tracers: n,
    steps,
    dtS,
    compared: errs.length,
    excludedRespawned: excluded,
    maxErrM: maxErr,
    p99ErrM: at(0.99),
    medianErrM: at(0.5),
    meanDisplacementM: errs.length === 0 ? Number.NaN : dispSum / errs.length,
    toleranceM: GPU_VALIDATION_TOLERANCE_M,
    pass: errs.length > 0 && maxErr <= GPU_VALIDATION_TOLERANCE_M,
  };
}
